import { TextInput, PlainButton } from "@jugl-web/ui-kit";
import {
  useFormFieldsValidators,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import React, { KeyboardEvent, useCallback, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { EmailSettingsFormValues } from "../EmailSettingsTab/types";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

export const BlockedContactsForAISection: React.FC = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { emailValidator } = useFormFieldsValidators();
  const { control, setValue, getValues, setError, clearErrors } =
    useFormContext<EmailSettingsFormValues>();

  const watchedBlockedEmails = useWatch({ control, name: "blocked_emails" });
  const blockedEmails = useMemo(
    () => watchedBlockedEmails ?? [],
    [watchedBlockedEmails]
  );

  const validatePendingEmail = useCallback(
    (value: string): true | string => {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return true;
      }

      const { pattern } = emailValidator();

      if (pattern && "value" in pattern && !pattern.value.test(trimmedValue)) {
        return pattern.message;
      }

      return true;
    },
    [emailValidator]
  );

  const addBlockedEmail = useCallback(() => {
    const normalizedEmail = getValues("pending_blocked_email")
      .trim()
      .toLowerCase();

    if (!normalizedEmail) {
      clearErrors("pending_blocked_email");
      return;
    }

    const validationResult = validatePendingEmail(normalizedEmail);

    if (validationResult !== true) {
      setError("pending_blocked_email", { message: validationResult });
      return;
    }

    if (blockedEmails.includes(normalizedEmail)) {
      toast(
        t({
          id: "client-support-page.email-already-blocked",
          defaultMessage: "This email is already blocked",
        }),
        { variant: "error" }
      );
      setValue("pending_blocked_email", "", { shouldDirty: false });
      clearErrors("pending_blocked_email");
      return;
    }

    setValue("blocked_emails", [...blockedEmails, normalizedEmail], {
      shouldDirty: true,
    });
    setValue("pending_blocked_email", "", { shouldDirty: false });
    clearErrors("pending_blocked_email");
  }, [
    blockedEmails,
    clearErrors,
    getValues,
    setError,
    setValue,
    t,
    toast,
    validatePendingEmail,
  ]);

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();
      addBlockedEmail();
    },
    [addBlockedEmail]
  );

  const handleRemoveEmail = useCallback(
    (email: string) => {
      setValue(
        "blocked_emails",
        blockedEmails.filter((blockedEmail) => blockedEmail !== email),
        { shouldDirty: true }
      );
    },
    [blockedEmails, setValue]
  );

  return (
    <CRMInfoSection
      title={t({
        id: "client-support-page.blocked-contacts-for-ai",
        defaultMessage: "Blocked Contacts for AI",
      })}
      subtitle={t({
        id: "client-support-page.blocked-contacts-for-ai-description",
        defaultMessage:
          "Emails of indicated addresses will be shown in Inbox, but will not get AI response",
      })}
    >
      <div className="flex flex-col gap-6">
        <Controller
          control={control}
          name="pending_blocked_email"
          rules={{
            validate: validatePendingEmail,
          }}
          render={({ field: { value, onChange, ref }, fieldState }) => (
            <TextInput
              classNames={{ wrapperClassName: "w-full" }}
              placeholder="example@company.com"
              value={value}
              ref={ref}
              onChange={(event) => {
                onChange(event.target.value.trim());
                clearErrors("pending_blocked_email");
              }}
              onKeyDown={handleInputKeyDown}
              onBlur={addBlockedEmail}
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        {blockedEmails.length > 0 && (
          <div className="flex flex-wrap gap-2.5">
            {blockedEmails.map((email) => (
              <div
                key={email}
                className="flex h-8 items-center gap-2.5 truncate rounded-xl bg-primary/10 px-3"
              >
                <span className="truncate font-secondary text-sm leading-[140%] font-medium text-dark-800">
                  {email}
                </span>
                <PlainButton onClick={() => handleRemoveEmail(email)}>
                  <CloseIcon />
                </PlainButton>
              </div>
            ))}
          </div>
        )}
      </div>
    </CRMInfoSection>
  );
};
